import { useCallback, useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { aws } from '../lib/awsdb';
import { vaultWrite } from '../lib/vaultWrite';
import { vaultListArchived } from '../lib/vaultRead';
import { ModalFrame } from '../ui/modal';
import { Avatar } from '../ui/avatar';
import { IconClose, IconHistory } from '../ui/icons';
import { CATEGORY_COLORS, groupByCategory } from './categories';

const LEVEL_LABEL = { viewer: 'Viewer', revealer: 'Revealer' };
const LEVEL_COLOR = { revealer: '#7c3aed', viewer: '#0F1419' };

// Verb phrase per action_type, so the Activity tab reads as a sentence
// ("revealed the password for Xero") instead of a raw enum + separate
// site column. Falls back to the de-underscored raw value for anything
// not in this list, so a new action_type doesn't silently disappear.
const ACTION_PHRASES = {
  credential_viewed: 'viewed',
  credential_revealed: 'revealed the password for',
  credential_copied: 'copied the password for',
  credential_created: 'created',
  credential_edited: 'edited',
  credential_archived: 'archived',
  credential_restored: 'restored',
};

function hueFromCode(code) {
  let h = 0;
  for (let i = 0; i < (code || '').length; i++) h = (h * 31 + code.charCodeAt(i)) % 360;
  return h;
}

function formatWhen(ts) {
  if (!ts) return '';
  const d = new Date(ts);
  return d.toLocaleString('en-AU', { day: 'numeric', month: 'short', year: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true }).toLowerCase();
}

// The access log, as its own modal — same treatment as Manage Access.
// Three tabs: Activity (event feed, open to anyone with Vault page access —
// the "who's done what" surface Mo asked Renz to use), Roster (admin-only —
// every vault-permitted staff member and their current grant per
// credential, even people with zero activity yet), and Archived
// (admin-only — soft-deleted credentials, restorable; matches the
// project-wide archive/never-hard-delete convention already used for
// POs/invoices/contacts).
export default function VaultActivityLogModal({ session, credentials, onClose, onRestored }) {
  const isAdmin = session?.role === 'admin';
  const [tab, setTab] = useState('activity');
  const [activity, setActivity] = useState([]);
  const [staff, setStaff] = useState([]);
  const [staffSearch, setStaffSearch] = useState('');
  const [selectedStaffCode, setSelectedStaffCode] = useState(null);
  const [allGrants, setAllGrants] = useState([]);
  const [archived, setArchived] = useState([]);
  const [archivedError, setArchivedError] = useState(null);
  const [restoringId, setRestoringId] = useState(null);

  const siteNameFor = useMemo(() => {
    const map = {};
    credentials.forEach(c => { map[c.id] = c.site_name; });
    return map;
  }, [credentials]);

  const loadActivity = useCallback(async () => {
    const { data } = await aws.from('vault_activity_log').select('*').order('created_at', { ascending: false }).limit(300);
    setActivity(data || []);
  }, []);

  useEffect(() => { loadActivity(); }, [loadActivity]);

  useEffect(() => {
    if (tab !== 'roster' || !isAdmin) return;
    // Every active staff member, same as Manage Access — not just people
    // who already have the `vault` page permission.
    aws.rpc('admin_staff_directory').then(({ data }) => setStaff((data || []).filter(s => s.active)));
    aws.from('vault_credential_grants').select('*').is('revoked_at', null).then(({ data }) => setAllGrants(data || []));
  }, [tab, isAdmin]);

  const loadArchived = useCallback(async () => {
    try {
      const { data } = await vaultListArchived(session);
      setArchived(data || []);
      setArchivedError(null);
    } catch (e) {
      setArchivedError(e.message || String(e));
    }
  }, [session]);

  useEffect(() => {
    if (tab !== 'archived' || !isAdmin) return;
    loadArchived();
  }, [tab, isAdmin, loadArchived]);

  async function restore(c) {
    setRestoringId(c.id);
    try {
      await vaultWrite(session, 'vault_credentials', 'update', { archived_at: null, archived_by: null }, { column: 'id', value: c.id });
      await loadArchived();
      onRestored?.();
    } catch (e) {
      alert('Could not restore: ' + (e.message || e));
    } finally {
      setRestoringId(null);
    }
  }

  const grantCountFor = useMemo(() => {
    const counts = {};
    allGrants.forEach(g => { counts[g.user_code] = (counts[g.user_code] || 0) + 1; });
    return counts;
  }, [allGrants]);

  const selectedStaff = staff.find(s => s.code === selectedStaffCode);
  const selectedGrants = useMemo(() => allGrants.filter(g => g.user_code === selectedStaffCode), [allGrants, selectedStaffCode]);
  const grantedCredentials = useMemo(() => credentials.filter(c => selectedGrants.some(g => g.credential_id === c.id)), [credentials, selectedGrants]);
  const groups = useMemo(() => groupByCategory(grantedCredentials), [grantedCredentials]);

  return (
    <ModalFrame onClose={onClose}>
      <motion.div
        role="dialog"
        initial={{ opacity: 0, y: 12, scale: 0.98 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: 12, scale: 0.98 }} transition={{ duration: 0.18 }}
        style={{
          width: 'min(720px, calc(100vw - 48px))',
          maxHeight: 'calc(100vh - 96px)',
          background: '#fff',
          borderRadius: 16,
          boxShadow: '0 24px 60px rgba(15,20,25,0.22), 0 2px 6px rgba(15,20,25,0.08)',
          display: 'flex', flexDirection: 'column', overflow: 'hidden',
        }}
      >
        <div style={{ padding: '16px 20px', borderBottom: '1px solid var(--border-subdued)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{ width: 30, height: 30, borderRadius: 8, background: 'var(--surface-inverted)', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <IconHistory size={14} />
            </div>
            <div>
              <div style={{ fontSize: 15, fontWeight: 600, color: 'var(--text-emphasized)' }}>Access Log</div>
              <div style={{ fontSize: 12, color: 'var(--text-subdued)', marginTop: 2 }}>Who's viewed, revealed, or copied a credential — and when</div>
            </div>
          </div>
          <button type="button" onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-subdued)', padding: 6, display: 'flex' }}>
            <IconClose size={16} />
          </button>
        </div>

        {isAdmin && (
          <div style={{ display: 'flex', gap: 4, padding: '10px 20px 0', flexShrink: 0 }}>
            {/* Archived tab hidden until moreece-vault ships `list_archived` — code below stays intact for when it does. */}
            {[{ key: 'activity', label: 'Activity' }, { key: 'roster', label: 'Roster' }].map(t => (
              <button key={t.key} type="button" onClick={() => setTab(t.key)}
                style={{
                  padding: '6px 14px', fontSize: 12, fontWeight: 600, borderRadius: '8px 8px 0 0', border: 'none', cursor: 'pointer', fontFamily: 'inherit',
                  background: tab === t.key ? 'var(--surface-elevated)' : 'transparent',
                  color: tab === t.key ? 'var(--text-emphasized)' : 'var(--text-subdued)',
                }}>{t.label}</button>
            ))}
          </div>
        )}

        {tab === 'activity' && (
          <div style={{ flex: 1, overflowY: 'auto' }}>
            {activity.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '48px 20px', color: 'var(--text-subdued)', fontSize: 13 }}>No access events yet</div>
            ) : (
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                <thead>
                  <tr>
                    {['Who', 'What happened', 'When'].map(h => (
                      <th key={h} style={{ position: 'sticky', top: 0, background: '#fff', textAlign: 'left', fontSize: 10, fontWeight: 600, color: 'var(--text-subdued)', textTransform: 'uppercase', letterSpacing: 'var(--tracking-caps)', padding: '8px 20px', borderBottom: '1px solid var(--border-subdued)', whiteSpace: 'nowrap' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {activity.map(entry => {
                    const site = siteNameFor[entry.credential_id];
                    const phrase = ACTION_PHRASES[entry.action_type] || entry.action_type?.replace(/_/g, ' ') || 'did something to';
                    return (
                      <tr key={entry.id}>
                        <td style={{ padding: '9px 20px', borderBottom: '1px solid var(--border-subdued)', fontWeight: 600, color: 'var(--text-emphasized)', whiteSpace: 'nowrap' }}>{entry.actor_name || 'Someone'}</td>
                        <td style={{ padding: '9px 20px', borderBottom: '1px solid var(--border-subdued)', color: 'var(--text-subdued)' }}>
                          {phrase}{' '}
                          <span style={{ fontWeight: 600, color: 'var(--text-emphasized)' }}>{site || 'a credential'}</span>
                          {entry.detail && <span> — {entry.detail}</span>}
                        </td>
                        <td style={{ padding: '9px 20px', borderBottom: '1px solid var(--border-subdued)', color: 'var(--text-subdued)', whiteSpace: 'nowrap' }}>{formatWhen(entry.created_at)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        )}

        {tab === 'roster' && isAdmin && (
          <div style={{ display: 'flex', flex: 1, minHeight: 0 }}>
            <div style={{ width: 220, flexShrink: 0, borderRight: '1px solid var(--border-subdued)', display: 'flex', flexDirection: 'column' }}>
              <div style={{ padding: 10, borderBottom: '1px solid var(--border-subdued)', flexShrink: 0 }}>
                <input type="text" placeholder="Filter staff…" value={staffSearch} onChange={e => setStaffSearch(e.target.value)}
                  className="form-input" style={{ width: '100%' }} />
              </div>
              <div style={{ overflowY: 'auto', flex: 1 }}>
                {staff
                  .filter(s => s.name?.toLowerCase().includes(staffSearch.trim().toLowerCase()))
                  .map(s => {
                    const staffIsAdmin = s.role === 'admin';
                    const selected = selectedStaffCode === s.code;
                    return (
                      <div key={s.code} onClick={() => !staffIsAdmin && setSelectedStaffCode(s.code)}
                        style={{
                          display: 'flex', alignItems: 'center', gap: 8,
                          padding: '8px 12px', fontSize: 12, fontWeight: selected ? 600 : 400,
                          cursor: staffIsAdmin ? 'default' : 'pointer', opacity: staffIsAdmin ? 0.5 : 1,
                          background: selected ? 'var(--color-primary-soft)' : 'transparent',
                          borderLeft: selected ? '2px solid var(--color-primary)' : '2px solid transparent',
                          borderBottom: '1px solid var(--border-subdued)', color: 'var(--text-emphasized)',
                        }}>
                        <Avatar name={s.name} hue={hueFromCode(s.code)} size={22} />
                        <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{s.name}</span>
                        {staffIsAdmin ? (
                          <span style={{ fontSize: 9, color: 'var(--text-disabled)', flexShrink: 0 }}>admin</span>
                        ) : (
                          <span style={{ fontSize: 10, color: 'var(--text-subdued)', flexShrink: 0 }}>{grantCountFor[s.code] || 0}</span>
                        )}
                      </div>
                    );
                  })}
              </div>
            </div>
            <div style={{ flex: 1, overflowY: 'auto' }}>
              {!selectedStaffCode ? (
                <div style={{ textAlign: 'center', padding: '48px 20px', color: 'var(--text-subdued)', fontSize: 13 }}>Pick a staff member to see what they currently have access to.</div>
              ) : groups.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '48px 20px', color: 'var(--text-subdued)', fontSize: 13 }}>{selectedStaff?.name} hasn't been granted access to anything yet.</div>
              ) : (<>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '12px 16px', borderBottom: '1px solid var(--border-subdued)' }}>
                  <Avatar name={selectedStaff?.name} hue={hueFromCode(selectedStaffCode)} size={22} />
                  <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-emphasized)' }}>{selectedStaff?.name}</span>
                </div>
                {groups.map(group => (
                  <div key={group.key}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 16px', background: 'var(--surface-elevated)' }}>
                      <span style={{ width: 7, height: 7, borderRadius: 999, background: group.color, flexShrink: 0 }} />
                      <span style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-emphasized)' }}>{group.label}</span>
                    </div>
                    {group.items.map(c => {
                      const level = selectedGrants.find(g => g.credential_id === c.id)?.level;
                      return (
                        <div key={c.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '9px 16px', borderTop: '1px solid var(--border-subdued)' }}>
                          <div style={{ fontSize: 12, fontWeight: 500, color: 'var(--text-emphasized)' }}>{c.site_name}</div>
                          <span style={{ fontSize: 10, fontWeight: 600, padding: '3px 9px', borderRadius: 999, background: LEVEL_COLOR[level], color: '#fff' }}>{LEVEL_LABEL[level] || level}</span>
                        </div>
                      );
                    })}
                  </div>
                ))}
              </>)}
            </div>
          </div>
        )}

        {tab === 'archived' && isAdmin && (
          <div style={{ flex: 1, overflowY: 'auto' }}>
            {archivedError ? (
              <div style={{ textAlign: 'center', padding: '32px 20px', color: 'var(--text-error)', fontSize: 12 }}>
                Couldn't load archived credentials: {archivedError}
                <div style={{ color: 'var(--text-subdued)', marginTop: 6 }}>This needs a `list_archived` action added to the moreece-vault Lambda — see VAULT-BACKEND-HANDOFF.md.</div>
              </div>
            ) : archived.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '48px 20px', color: 'var(--text-subdued)', fontSize: 13 }}>Nothing archived — deleted credentials show up here, recoverable.</div>
            ) : (
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                <thead>
                  <tr>
                    {['Site', 'Category', 'Archived By', 'When', ''].map(h => (
                      <th key={h} style={{ textAlign: 'left', fontSize: 10, fontWeight: 600, color: 'var(--text-subdued)', textTransform: 'uppercase', letterSpacing: 'var(--tracking-caps)', padding: '8px 20px', borderBottom: '1px solid var(--border-subdued)', whiteSpace: 'nowrap' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {archived.map(c => (
                    <tr key={c.id}>
                      <td style={{ padding: '9px 20px', borderBottom: '1px solid var(--border-subdued)', fontWeight: 500, color: 'var(--text-emphasized)' }}>{c.site_name}</td>
                      <td style={{ padding: '9px 20px', borderBottom: '1px solid var(--border-subdued)' }}>
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, color: 'var(--text-subdued)' }}>
                          <span style={{ width: 7, height: 7, borderRadius: 999, background: CATEGORY_COLORS[c.category] || '#64748b', flexShrink: 0 }} />
                          {c.category || 'Other'}
                        </span>
                      </td>
                      <td style={{ padding: '9px 20px', borderBottom: '1px solid var(--border-subdued)', color: 'var(--text-subdued)', whiteSpace: 'nowrap' }}>{c.archived_by || '--'}</td>
                      <td style={{ padding: '9px 20px', borderBottom: '1px solid var(--border-subdued)', color: 'var(--text-subdued)', whiteSpace: 'nowrap' }}>{formatWhen(c.archived_at)}</td>
                      <td style={{ padding: '9px 20px', borderBottom: '1px solid var(--border-subdued)' }}>
                        <button type="button" className="btn btn-secondary btn-sm" disabled={restoringId === c.id} onClick={() => restore(c)}>
                          {restoringId === c.id ? 'Restoring…' : 'Restore'}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}
      </motion.div>
    </ModalFrame>
  );
}
